import '@fontsource/montserrat/300.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/800.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { ThemeOptions } from '@mui/material';

export const colors = {
    blue: '#53A5EA',
    gold: '#C98508',
    green: '#00A739',
    orange: '#FAAD19',
    primary: '#F9C132',
    purple: '#6F52ED',
    red: '#E24636',
    yellow: '#F8E71C',
};

const colorClasses: any = {};
Object.entries(colors).forEach(([name, value]) => {
    colorClasses[`.text-${name}`] = {
        color: value,
        fontWeight: 700,
    };
});

export const palettes = {
    dark: {
        background: {
            default: '#3C3B3B',
            paper: '#1E1E1E',
        },
    },
    light: {
        text: {
            primary: '#141414',
        },
    },
};

export const theme: ThemeOptions = {
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 992,
            lg: 1200,
            xl: 1536,
        },
    },
    components: {
        MuiButton: {
            defaultProps: {
                disableElevation: true,
                variant: 'contained',
            },
            styleOverrides: {
                root: {
                    fontSize: 17,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    lineHeight: 1,
                    padding: 12,
                    textTransform: 'none',
                },
                sizeLarge: {
                    borderRadius: 50,
                    fontSize: 20,
                    fontWeight: 700,
                    padding: '22px 60px',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    '&.MuiButton-containedSecondary': {
                        backgroundColor: '#FFFFFF',
                        padding: '18px 60px',
                        '&:hover': {
                            backgroundColor: '#D3D3D3',
                        },
                    },
                },
                text: {
                    border: '3px solid transparent',
                    borderRadius: 50,
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '3px 9px',
                    position: 'relative',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    '&:focus, &:hover': {
                        backgroundColor: '#365838',
                        border: '3px solid #F9C132',
                        color: '#F9C132',
                        zIndex: 2,
                    },
                    '&.compact': {
                        '.compact-inner': {
                            opacity: 0,
                            transition: 'opacity 0.4s',
                        },
                        '&:focus .compact-inner, &:hover .compact-inner': {
                            opacity: 1,
                        },
                    },
                    '&.fill': {
                        backgroundColor: '#365838',
                    },
                    '&.info': {
                        paddingRight: 28,
                    },
                    '&.static': {
                        color: '#FFFFFF',
                        pointerEvents: 'none',
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                ...colorClasses,
                hr: {
                    margin: 0,
                },
                ol: {
                    marginY: 0,
                    paddingLeft: 24,
                },
                p: {
                    margin: 0,
                },
                picture: {
                    display: 'block',
                    lineHeight: 0,
                },
                ul: {
                    margin: 0,
                    paddingLeft: 24,
                },
                '.nowrap': {
                    whiteSpace: 'nowrap',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&.info': {
                        color: '#5D9562',
                        marginLeft: 4,
                        padding: 0,
                        transition: 'background-color 0.4s, color 0.4s',
                        '&:focus, &:hover': {
                            color: '#F9C132',
                        },
                        '&:active': {
                            backgroundColor: '#507E53',
                        },
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FFF',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    cursor: 'pointer',
                },
            },
        },
        MuiTableRow: {
            defaultProps: {
                hover: true,
            },
        },
    },
    palette: {
        ...colors,
        action: {
            disabledBackground: '#D8D8D8',
            disabled: '#979797',
        },
        error: {
            main: '#DE4F3B',
        },
        primary: {
            main: '#F9C132',
            light: '#FFE9BF',
        },
        secondary: {
            main: '#EBECEF',
        },
        success: {
            main: '#00A739',
        },
        warning: {
            main: '#EE9810',
        },
    },
    typography: {
        // Defaults
        body1: {
            fontSize: 18,
            fontWeight: 400,
            lineHeight: 1.444,
            letterSpacing: 0.75,
        },
        fontFamily: 'Montserrat, sans-serif',

        // Montserrat
        montserrat314: {
            fontSize: 14,
            fontWeight: 300,
            lineHeight: 1,
            letterSpacing: 0.75,
        },
        montserrat413: {
            fontSize: 13,
            fontWeight: 400,
            lineHeight: 1.2,
        },
        montserrat414: {
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.2,
        },
        montserrat415: {
            fontSize: 15,
            fontWeight: 400,
            lineHeight: 1.2,
        },
        montserrat418: {
            fontSize: 18,
            fontWeight: 400,
            lineHeight: 1.44,
        },
        montserrat420: {
            fontSize: 20,
            fontWeight: 400,
        },
        montserrat514: {
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.43,
        },
        montserrat518: {
            fontSize: 18,
            fontWeight: 500,
            lineHeight: 1.2,
        },
        montserrat524: {
            fontSize: 24,
            fontWeight: 500,
            lineHeight: 1.083,
        },
        montserrat528: {
            fontSize: 28,
            fontWeight: 500,
            lineHeight: 0.857,
        },
        montserrat529: {
            fontSize: 29,
            fontWeight: 500,
            lineHeight: 1.345,
        },
        montserrat614: {
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.345,
        },
        montserrat616: {
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 1.875,
        },
        montserrat618: {
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 1.345,
        },
        montserrat620: {
            fontSize: 20,
            fontWeight: 600,
            lineHeight: 1.345,
        },
        montserrat712: {
            fontSize: 12,
            fontWeight: 700,
        },
        montserrat714: {
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.444,
        },
        montserrat718: {
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.444,
        },
        montserrat720: {
            fontSize: 20,
            fontWeight: 700,
        },
        montserrat730: {
            fontSize: 30,
            fontWeight: 700,
        },
        montserrat732: {
            fontSize: 32,
            fontWeight: 700,
        },
        montserrat828: {
            fontSize: 28,
            fontWeight: 800,
        },
        montserrat840: {
            fontSize: 40,
            fontWeight: 800,
            lineHeight: 1,
        },
        montserrat848: {
            fontSize: 48,
            fontWeight: 800,
            lineHeight: 1,
        },

        // Roboto
        roboto511: {
            fontSize: 11,
            fontWeight: 500,
            fontFamily: 'Roboto, sans-serif',
        },
        roboto714: {
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'Roboto, sans-serif',
            lineHeight: 1,
        },
    },
};
